import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, CheckSquare, Loader2, Lock } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { ImageUploader } from "@/components/ImageUploader";
import { Countdown } from "@/components/Countdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPE_LABEL = { marcatore: "Marcatore", risultato: "Risultato esatto", combinato: "Combinato", "1x2": "Esito 1X2 (schedina)", goalnogoal: "Goal/No Goal (schedina)" };

const SCHEDINA_TYPES = ["1x2", "goalnogoal"];
const SCHEDINA_OPTS = {
  "1x2": [{ v: "1", l: "1" }, { v: "X", l: "X" }, { v: "2", l: "2" }],
  goalnogoal: [{ v: "goal", l: "Goal" }, { v: "nogoal", l: "No Goal" }],
};

const emptyForm = () => ({
  question: "", image_path: null, points: 10, deadline: "", period_id: "", type: "marcatore",
  options: [{ text: "", image_path: null }, { text: "", image_path: null }],
  matches: [{ home_team: "", away_team: "", image_path: null }],
  notify: true,
});

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function DateTimeField({ value, onChange }) {
  const date = value ? value.slice(0, 10) : "";
  const time = value ? value.slice(11, 16) : "";
  const todayStr = new Date().toISOString().slice(0, 10);
  return (
    <div className="grid grid-cols-2 gap-2">
      <Input data-testid="quiz-deadline-date" type="date" value={date}
        onChange={(e) => onChange(`${e.target.value}T${time || "23:59"}`)} className="rounded-xl" />
      <Input data-testid="quiz-deadline-time" type="time" value={time}
        onChange={(e) => onChange(`${date || todayStr}T${e.target.value}`)} className="rounded-xl" />
    </div>
  );
}

export function AdminPredictions({ vip = false }) {
  const [preds, setPreds] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveIds, setResolveIds] = useState([]);
  const [resolveScore, setResolveScore] = useState({ home: "", away: "" });
  const [resolveMatches, setResolveMatches] = useState({});

  const load = async () => {
    const [{ data: p }, { data: per }] = await Promise.all([
      api.get(`/admin/predictions?vip=${vip ? 1 : 0}`), api.get(`/admin/periods?vip=${vip ? 1 : 0}`),
    ]);
    setPreds(p); setPeriods(per);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), period_id: periods.find((x) => x.status === "active")?.id || "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      question: p.question, image_path: p.image_path, points: p.points, type: p.type || "marcatore",
      deadline: toLocalInput(p.deadline), period_id: p.period_id || "",
      options: (p.options || []).map((o) => ({ id: o.id, text: o.text, image_path: o.image_path })),
      matches: (p.matches || []).length
        ? p.matches.map((m) => ({ id: m.id, home_team: m.home_team, away_team: m.away_team, image_path: m.image_path }))
        : [{ home_team: "", away_team: "", image_path: null }],
    });
    setOpen(true);
  };

  const setOpt = (i, key, val) => {
    const options = [...form.options];
    options[i] = { ...options[i], [key]: val };
    setForm({ ...form, options });
  };
  const addOpt = () => setForm({ ...form, options: [...form.options, { text: "", image_path: null }] });
  const removeOpt = (i) => setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) });

  const showOptions = form.type === "marcatore" || form.type === "combinato";
  const isSchedina = SCHEDINA_TYPES.includes(form.type);

  const setMatch = (i, key, val) => {
    const matches = [...form.matches];
    matches[i] = { ...matches[i], [key]: val };
    setForm({ ...form, matches });
  };
  const addMatch = () => setForm({ ...form, matches: [...form.matches, { home_team: "", away_team: "", image_path: null }] });
  const removeMatch = (i) => setForm({ ...form, matches: form.matches.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.question.trim()) return toast.error("Inserisci la domanda.");
    if (!form.deadline) return toast.error("Imposta una scadenza.");
    let opts = [];
    if (showOptions) {
      opts = form.options.filter((o) => o.text.trim());
      if (opts.length < 2) return toast.error("Servono almeno 2 opzioni (marcatori).");
    }
    let matches = [];
    if (isSchedina) {
      matches = form.matches.filter((m) => m.home_team.trim() && m.away_team.trim());
      if (matches.length < 1) return toast.error("Aggiungi almeno un match alla schedina.");
    }
    setSaving(true);
    const payload = {
      question: form.question, image_path: form.image_path, type: form.type,
      points: Number(form.points), deadline: new Date(form.deadline).toISOString(),
      period_id: form.period_id || null, options: opts, matches,
      is_vip: vip, notify: form.notify !== false,
    };
    try {
      if (editing) await api.put(`/admin/predictions/${editing}`, payload);
      else await api.post("/admin/predictions", payload);
      toast.success(editing ? "Pronostico aggiornato" : "Pronostico creato");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo pronostico e tutte le risposte?")) return;
    await api.delete(`/admin/predictions/${id}`);
    toast.success("Eliminato"); load();
  };

  const openResolve = (p) => {
    setResolveTarget(p);
    setResolveIds(p.correct_option_ids || []);
    setResolveScore({
      home: p.correct_home_score != null ? String(p.correct_home_score) : "",
      away: p.correct_away_score != null ? String(p.correct_away_score) : "",
    });
    const mr = {};
    (p.matches || []).forEach((m) => { if (m.correct_outcome) mr[m.id] = m.correct_outcome; });
    setResolveMatches(mr);
  };
  const doResolve = async () => {
    const t = resolveTarget.type || "marcatore";
    const payload = {};
    if (t === "marcatore" || t === "combinato") {
      if (resolveIds.length === 0) return toast.error("Seleziona almeno un marcatore corretto.");
      payload.correct_option_ids = resolveIds;
    }
    if (t === "risultato" || t === "combinato") {
      if (resolveScore.home === "" || resolveScore.away === "") return toast.error("Inserisci il risultato esatto corretto.");
      payload.home_score = Number(resolveScore.home);
      payload.away_score = Number(resolveScore.away);
    }
    if (t === "1x2" || t === "goalnogoal") {
      const matches = resolveTarget.matches || [];
      if (matches.some((m) => !resolveMatches[m.id])) return toast.error("Inserisci l'esito reale di tutti i match.");
      payload.match_results = resolveMatches;
    }
    try {
      const { data } = await api.post(`/admin/predictions/${resolveTarget.id}/resolve`, payload);
      toast.success(`Punti assegnati a ${data.users_awarded} utenti`);
      setResolveTarget(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (preds === null) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>;

  const rType = resolveTarget?.type || "marcatore";

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl font-extrabold uppercase text-zinc-950">Pronostici</h2>
        <Button data-testid="admin-create-quiz-button" onClick={openCreate} className="rounded-full bg-green-500 font-bold text-zinc-950 hover:bg-green-600">
          <Plus className="mr-1 h-4 w-4" /> Nuovo
        </Button>
      </div>

      <div className="space-y-3">
        {preds.length === 0 && <p className="text-sm text-zinc-500">Nessun pronostico. Creane uno!</p>}
        {preds.map((p) => (
          <div key={p.id} data-testid={`admin-prediction-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                {p.image_path && <img src={fileUrl(p.image_path)} alt="" className="h-14 w-14 rounded-xl object-cover" />}
                <div>
                  <p className="font-display text-lg font-bold uppercase leading-tight text-zinc-950">{p.question}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <span className="rounded-full bg-zinc-100 px-2 py-0.5 font-bold text-zinc-700">{TYPE_LABEL[p.type || "marcatore"]}</span>
                    <span className="rounded-full bg-green-100 px-2 py-0.5 font-bold text-green-700">+{p.points} pt</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">{p.answers_count} risposte</span>
                    {p.status === "resolved"
                      ? <span className="rounded-full bg-zinc-950 px-2 py-0.5 font-bold text-green-400">Risolto</span>
                      : <span className="flex items-center gap-1">Scade: <Countdown deadline={p.deadline} className="text-xs" /></span>}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button data-testid={`admin-resolve-quiz-button-${p.id}`} onClick={() => openResolve(p)} size="sm" variant="ghost" className="text-green-600" title="Risolvi"><CheckSquare className="h-4 w-4" /></Button>
                <Button onClick={() => openEdit(p)} size="sm" variant="ghost" title="Modifica"><Pencil className="h-4 w-4" /></Button>
                <Button data-testid={`admin-delete-quiz-${p.id}`} onClick={() => del(p.id)} size="sm" variant="ghost" className="text-red-500" title="Elimina"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black uppercase">{editing ? "Modifica" : "Nuovo"} pronostico</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Tipo di pronostico</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="quiz-type-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="marcatore">Marcatore (risposta multipla)</SelectItem>
                  <SelectItem value="risultato">Risultato esatto (l'utente inserisce il punteggio)</SelectItem>
                  <SelectItem value="combinato">Combinato (risultato + marcatore)</SelectItem>
                  <SelectItem value="1x2">Esito 1X2 (schedina multi-match)</SelectItem>
                  <SelectItem value="goalnogoal">Goal/No Goal (schedina multi-match)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Domanda</Label>
              <Textarea data-testid="quiz-question-input" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="Es. Chi segnerà in Roma-Inter?" className="rounded-xl" />
            </div>
            <ImageUploader label="Locandina domanda (opzionale)" value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v })} testId="quiz-question-image" />
            <div className="space-y-1.5">
              <Label>Punti</Label>
              <Input data-testid="quiz-points-input" type="number" min={1} value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Scadenza {isSchedina ? "(unica per tutta la schedina)" : ""}</Label>
              <DateTimeField value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} />
            </div>
            <div className="space-y-1.5">
              <Label>Periodo/Classifica</Label>
              <Select value={form.period_id || "none"} onValueChange={(v) => setForm({ ...form, period_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="quiz-period-select" className="rounded-xl"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun periodo</SelectItem>
                  {periods.map((per) => <SelectItem key={per.id} value={per.id}>{per.name} {per.status === "active" ? "(attivo)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {isSchedina ? (
              <div className="space-y-3">
                <Label>Match della schedina ({form.type === "1x2" ? "esiti 1 · X · 2 automatici" : "Goal / No Goal automatici"})</Label>
                {form.matches.map((m, i) => (
                  <div key={i} className="space-y-2 rounded-2xl border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-zinc-500">Match {i + 1}</span>
                      {form.matches.length > 1 && <Button type="button" onClick={() => removeMatch(i)} size="sm" variant="ghost" className="text-red-500"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                    <ImageUploader label="Locandina match (opzionale)" value={m.image_path} onChange={(v) => setMatch(i, "image_path", v)} testId={`match-image-${i}`} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid={`match-home-${i}`} value={m.home_team} onChange={(e) => setMatch(i, "home_team", e.target.value)} placeholder="Squadra casa" className="rounded-xl" />
                      <Input data-testid={`match-away-${i}`} value={m.away_team} onChange={(e) => setMatch(i, "away_team", e.target.value)} placeholder="Squadra ospite" className="rounded-xl" />
                    </div>
                  </div>
                ))}
                <Button type="button" data-testid="add-match-button" onClick={addMatch} variant="outline" size="sm" className="rounded-full"><Plus className="mr-1 h-3 w-3" /> Aggiungi match</Button>
              </div>
            ) : form.type === "risultato" ? (
              <p className="rounded-2xl bg-slate-100 p-3 text-sm text-zinc-600">Nessuna opzione: l'utente inserirà lui stesso il punteggio (gol casa e gol ospite).</p>
            ) : (
              <div className="space-y-2">
                <Label>{form.type === "combinato" ? "Marcatori (l'utente sceglie 1 + inserisce il risultato)" : "Opzioni di risposta"}</Label>
                {form.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <ImageUploader compact value={o.image_path} onChange={(v) => setOpt(i, "image_path", v)} testId={`quiz-option-image-${i}`} />
                    <Input data-testid={`quiz-option-text-${i}`} value={o.text} onChange={(e) => setOpt(i, "text", e.target.value)} placeholder={`Opzione ${i + 1}`} className="flex-1 rounded-xl" />
                    {form.options.length > 2 && <Button type="button" onClick={() => removeOpt(i)} size="sm" variant="ghost" className="text-red-500"><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                ))}
                <Button type="button" data-testid="quiz-add-option" onClick={addOpt} variant="outline" size="sm" className="rounded-full"><Plus className="mr-1 h-3 w-3" /> Aggiungi opzione</Button>
              </div>
            )}
          </div>
          <label className="mt-2 flex items-center gap-3 rounded-xl bg-slate-100 p-3">
            <Checkbox data-testid="quiz-notify-checkbox" checked={form.notify !== false} onCheckedChange={(v) => setForm({ ...form, notify: !!v })} />
            <span className="text-sm font-semibold text-zinc-700">Invia notifica push agli utenti</span>
          </label>
          <DialogFooter>
            <Button data-testid="quiz-save-button" onClick={save} disabled={saving} className="w-full rounded-full bg-zinc-950 font-bold text-green-400 hover:bg-zinc-800">
              {saving ? "Salvataggio..." : editing ? "Salva modifiche" : "Crea pronostico"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve dialog */}
      <Dialog open={!!resolveTarget} onOpenChange={(v) => !v && setResolveTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black uppercase">Risolvi pronostico</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">Inserisci l'esito reale: i punti verranno assegnati automaticamente.</p>

          {(rType === "risultato" || rType === "combinato") && (
            <div className="flex items-center justify-center gap-3 rounded-2xl bg-slate-100 p-4">
              <div className="text-center">
                <p className="mb-1 text-[10px] font-bold uppercase text-zinc-500">Casa</p>
                <Input data-testid="resolve-home-score" type="number" min={0} value={resolveScore.home}
                  onChange={(e) => setResolveScore({ ...resolveScore, home: e.target.value })} className="h-12 w-14 rounded-xl text-center font-mono-t text-xl font-black" />
              </div>
              <span className="font-display text-2xl font-black text-zinc-400">-</span>
              <div className="text-center">
                <p className="mb-1 text-[10px] font-bold uppercase text-zinc-500">Ospite</p>
                <Input data-testid="resolve-away-score" type="number" min={0} value={resolveScore.away}
                  onChange={(e) => setResolveScore({ ...resolveScore, away: e.target.value })} className="h-12 w-14 rounded-xl text-center font-mono-t text-xl font-black" />
              </div>
            </div>
          )}

          {(rType === "marcatore" || rType === "combinato") && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Marcatore/i corretto/i</Label>
              {resolveTarget?.options.map((o) => (
                <label key={o.id} data-testid={`resolve-option-${o.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <Checkbox checked={resolveIds.includes(o.id)}
                    onCheckedChange={(v) => setResolveIds(v ? [...resolveIds, o.id] : resolveIds.filter((x) => x !== o.id))} />
                  {o.image_path && <img src={fileUrl(o.image_path)} alt="" className="h-8 w-8 rounded object-cover" />}
                  <span className="font-semibold text-zinc-900">{o.text}</span>
                </label>
              ))}
            </div>
          )}

          {(rType === "1x2" || rType === "goalnogoal") && (
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Esito reale di ogni match</Label>
              {(resolveTarget?.matches || []).map((m) => (
                <div key={m.id} className="rounded-xl border border-slate-200 p-3">
                  <p className="mb-2 text-sm font-bold text-zinc-900">{m.home_team} <span className="text-zinc-400">vs</span> {m.away_team}</p>
                  <div className={`grid gap-2 ${rType === "1x2" ? "grid-cols-3" : "grid-cols-2"}`}>
                    {SCHEDINA_OPTS[rType].map((o) => (
                      <button key={o.v} type="button" data-testid={`resolve-match-${m.id}-${o.v}`}
                        onClick={() => setResolveMatches((s) => ({ ...s, [m.id]: o.v }))}
                        className={`rounded-xl border-2 py-2 text-sm font-black uppercase ${resolveMatches[m.id] === o.v ? "border-green-600 bg-green-50 text-green-700" : "border-slate-200 bg-white text-zinc-600"}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button data-testid="resolve-confirm-button" onClick={doResolve} className="w-full rounded-full bg-green-500 font-bold text-zinc-950 hover:bg-green-600">
              <Lock className="mr-1 h-4 w-4" /> Assegna punti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
